import { useQuery } from '@tanstack/react-query';
import { fetchAranacaklarContacts, fetchAranacaklarPortalFilters } from '../lib/aranacaklar-api.js';

const EXTRA_KEYS_FROM_PORTAL_FILTERS = [
  'meta_min',
  'meta_max',
  'sort_by',
  'sort_dir',
  'gm_min',
  'gm_max',
  'puan_min',
  'puan_max',
  'listing_attr',
  'expert_answered',
  'price_advantage_only',
  'max_nav_city_m',
  'max_nav_town_m',
  'min_nav_city_m',
  'min_nav_town_m',
  'road_frontage_min_m',
  'road_frontage_max_m',
  'listing_has_road',
  'listing_has_water',
  'listing_has_power_line',
  'unit_price_min',
  'unit_price_max',
  'hisseli',
  'category_type',
];

function mapPortalFiltersToPatch(apiFilters) {
  const f = apiFilters && typeof apiFilters === 'object' ? apiFilters : {};
  const patch = {};
  const str = (v) => (v === undefined || v === null ? '' : String(v));
  if (f.category_main != null && f.category_main !== '') patch.category_main = str(f.category_main);
  if (f.category_leaf_id != null && f.category_leaf_id !== '') patch.category_leaf_id = str(f.category_leaf_id);
  if (f.area_m2_min != null && f.area_m2_min !== '') patch.area_m2_min = str(f.area_m2_min);
  if (f.area_m2_max != null && f.area_m2_max !== '') patch.area_m2_max = str(f.area_m2_max);
  if (f.total_price_min != null && f.total_price_min !== '') patch.total_price_min = str(f.total_price_min);
  if (f.total_price_max != null && f.total_price_max !== '') patch.total_price_max = str(f.total_price_max);
  if (f.city_id != null && f.city_id !== '') patch.city_id = str(f.city_id);
  if (f.town_id != null && f.town_id !== '') patch.town_id = str(f.town_id);
  if (f.town_ids != null && f.town_ids !== '') patch.town_ids = str(f.town_ids);
  if (f.quarter_ids != null && f.quarter_ids !== '') patch.quarter_ids = str(f.quarter_ids);
  if (f.is_sale != null && f.is_sale !== '') patch.is_sale = str(f.is_sale);
  if (f.listing_type != null && f.listing_type !== '') patch.listing_type = str(f.listing_type);
  for (const k of EXTRA_KEYS_FROM_PORTAL_FILTERS) {
    if (f[k] != null && f[k] !== '') patch[k] = str(f[k]);
  }
  if (!patch.town_id) patch.town_id = '';
  if (!patch.quarter_id) patch.quarter_id = '';
  return patch;
}

/**
 * Son sorgular sol panel — kayıtlı kişi ve intent’ten gelen filtre parametreleri taslağa yazılır;
 * liste «Sonuçları Güncelle» ile güncellenir.
 */
export function AranacaklarQuickFilter({
  disabled,
  selectedContactId,
  onApplyPatch,
  contactsFetchEnabled = true,
}) {
  const contactsQuery = useQuery({
    queryKey: ['aranacaklar-quick-filter'],
    queryFn: fetchAranacaklarContacts,
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    /** Oturum varlığı #pp-user ile değil, API yanıtı ile anlaşılır (localhost / 127.0.0.1 çerez farkı). */
    enabled: contactsFetchEnabled,
  });

  const rows = contactsQuery.data || [];
  const options = rows
    .map((r) => (r && typeof r === 'object' && r.contact ? r.contact : r))
    .filter(Boolean);

  async function onChange(ev) {
    const id = String(ev.target.value || '').trim();
    if (!id) {
      onApplyPatch({
        aranacaklar_contact_id: '',
        aranacaklar_contact_name: '',
      });
      return;
    }
    const contact = options.find((c) => String(c.contact_id) === id);
    const name = contact?.full_name || id;
    try {
      const data = await fetchAranacaklarPortalFilters(id);
      const mapped = mapPortalFiltersToPatch(data.filters);
      onApplyPatch({
        ...mapped,
        aranacaklar_contact_id: id,
        aranacaklar_contact_name: name,
        page: 1,
      });
    } catch {
      onApplyPatch({
        aranacaklar_contact_id: id,
        aranacaklar_contact_name: name,
        page: 1,
      });
    }
  }

  const loading = contactsFetchEnabled && contactsQuery.isLoading;
  const hintClass = 'pp-aranacaklar-quick-hint';
  const unauthorized = contactsQuery.isError && contactsQuery.error?.code === 'UNAUTHORIZED';
  const loginNext = typeof window !== 'undefined' ? encodeURIComponent(window.location.pathname + window.location.search) : '';

  return (
    <>
      <div className="pp-filter-group-title">Müşteri Filtresi</div>
      <label className="pp-filter-field">
        <select
          value={selectedContactId || ''}
          disabled={disabled || loading}
          onChange={onChange}
          onFocus={() => {
            if (contactsFetchEnabled) void contactsQuery.refetch();
          }}
          aria-label="Kayıtlı kişi ile filtrele"
        >
          <option value="">
            {loading ? 'Yükleniyor…' : 'Kayıtlı kişi seçin…'}
          </option>
          {options.map((c, idx) => {
            const id = String(c.contact_id ?? '');
            return (
              <option key={id || `aranacaklar-opt-${idx}`} value={id}>
                {c.full_name || id || '—'}
              </option>
            );
          })}
        </select>
      </label>
      {!contactsFetchEnabled ? (
        <p className={hintClass}>Kayıtlı kişiler bu panelde devre dışı.</p>
      ) : null}
      {contactsFetchEnabled && unauthorized ? (
        <p className={hintClass} role="alert">
          Oturum gerekli: Son sorgularda kayıtlı kişileri görmek için aynı tarayıcıda{' '}
          <a href={loginNext ? `/accounts/login/?next=${loginNext}` : '/accounts/login/'}>giriş yapın</a>.
          Cursor ile kendi tarayıcınız farklı çerez kullanır; adres olarak{' '}
          <strong>localhost</strong> ile <strong>127.0.0.1</strong> karıştırmayın.
        </p>
      ) : null}
      {contactsFetchEnabled && contactsQuery.isError && !unauthorized ? (
        <p className={hintClass} role="alert">
          {contactsQuery.error?.message || 'Kişi listesi yüklenemedi.'} Gerekirse sayfayı yenileyin.
        </p>
      ) : null}
    </>
  );
}
